import { Tooltip } from '@/ui/tooltip';
import { cn } from '@/lib/cn';

/**
 * OAuth buttons.
 *
 * Present because both login mockups show them, DISABLED because this build is credentials-only
 * by decision - no OAuth flow exists on the backend. They are rendered rather than dropped so the
 * login screens match their mockups, and the tooltip says plainly why they do nothing.
 */
type Provider = 'google' | 'microsoft' | 'apple';

const LABELS: Record<Provider, string> = {
  google: 'Continue with Google',
  microsoft: 'Continue with Microsoft',
  apple: 'Continue with Apple'
};

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path
        fill="#4285F4"
        d="M23 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.2a5.3 5.3 0 0 1-2.3 3.5v2.9h3.7c2.2-2 3.4-5 3.4-8.6Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.1 0 5.7-1 7.6-2.8l-3.7-2.9c-1 .7-2.3 1.1-3.9 1.1-3 0-5.5-2-6.4-4.7H1.8v3a12 12 0 0 0 10.2 6.3Z"
      />
      <path fill="#FBBC05" d="M5.6 14.7a7.2 7.2 0 0 1 0-4.6v-3H1.8a12 12 0 0 0 0 10.6l3.8-3Z" />
      <path fill="#EA4335" d="M12 4.8c1.7 0 3.2.6 4.4 1.7l3.2-3.2A12 12 0 0 0 1.8 7.1l3.8 3C6.5 7.4 9 4.8 12 4.8Z" />
    </svg>
  );
}

function MicrosoftGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path fill="#F25022" d="M2 2h9.4v9.4H2z" />
      <path fill="#7FBA00" d="M12.6 2H22v9.4h-9.4z" />
      <path fill="#00A4EF" d="M2 12.6h9.4V22H2z" />
      <path fill="#FFB900" d="M12.6 12.6H22V22h-9.4z" />
    </svg>
  );
}

function AppleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="size-4 fill-foreground" aria-hidden>
      <path d="M16.4 12.8c0-2.5 2-3.7 2.1-3.8-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.6.9-.8 0-1.9-.9-3.1-.8-1.6 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.8-2.3.9-1.3 1.3-2.6 1.3-2.6s-2.4-.9-2.4-3.4ZM14.1 5.6c.6-.8 1.1-1.9 1-3-1 0-2.1.6-2.8 1.5-.6.7-1.1 1.9-1 3 1.1.1 2.2-.6 2.8-1.5Z" />
    </svg>
  );
}

const GLYPHS: Record<Provider, () => React.ReactElement> = {
  google: GoogleGlyph,
  microsoft: MicrosoftGlyph,
  apple: AppleGlyph
};

export function OAuthButtons({ providers, className }: { providers: Provider[]; className?: string }) {
  return (
    <div className={cn('grid gap-2.5', className)}>
      {providers.map(provider => {
        const Glyph = GLYPHS[provider];
        return (
          <Tooltip key={provider} label="Credentials sign-in only in this build">
            <button
              type="button"
              disabled
              aria-disabled
              className={cn(
                'flex h-11 w-full cursor-not-allowed items-center justify-center gap-2.5',
                'rounded border border-border bg-inset text-[13px] text-foreground opacity-60'
              )}
            >
              <Glyph />
              {LABELS[provider]}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}
